/**
 * Wave 27.2 — Public customer-facing portal for Quotes / Invoices (Phase C).
 *
 * Resolves a share-token via the new /api/portal/quote/{token} or
 * /api/portal/invoice/{token} endpoint (backend scrubs every internal /
 * cost-margin field). Renders a customer-friendly, light-mode view.
 *
 * RULE: This page is the only public, unauthenticated surface in the
 * system. It must never display project_id, internal_notes, supplier
 * margin links, or any cost-side data.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Loader2, AlertTriangle, Download, FileText, Receipt as ReceiptIcon,
  Calendar, DollarSign, ExternalLink, CheckCircle2,
} from "lucide-react";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";

const BASE = BACKEND_URL;

const fmtMoney = (n, ccy = "ZAR") =>
  n == null ? "—" : `${ccy} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PortalDocument({ kind }) {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BASE}/api/portal/${kind}/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        const code = e?.response?.status;
        const msg = e?.response?.data?.detail || "Couldn't load this document.";
        setError({ code, msg });
      })
      .finally(() => setLoading(false));
  }, [kind, token]);

  if (loading) return (
    <div className="min-h-screen bg-[#f7f8fb] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#1FB6C1]" />
    </div>
  );
  const supportEmail = data?.branding?.support_email || error?.support_email || "support@geminii-ai.com";
  const brandName = data?.branding?.app_name || "your service team";
  if (error) return (
    <div className="min-h-screen bg-[#f7f8fb] flex items-center justify-center p-6">
      <div className="bg-white border border-rose-200 rounded-lg p-6 max-w-md text-center shadow-sm" data-testid="portal-error">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-rose-500" />
        <div className="font-semibold text-gray-900">
          {error.code === 410 ? "This link has expired." : "Document not available."}
        </div>
        <div className="text-sm text-gray-500 mt-1">{error.msg}</div>
        <div className="text-xs text-gray-400 mt-4">
          Need a new link? Reply to the email you received or contact {brandName} at <a className="underline" href={`mailto:${supportEmail}`}>{supportEmail}</a>.
        </div>
      </div>
    </div>
  );
  if (!data) return null;

  const { doc, customer } = data;
  const isInvoice = kind === "invoice";
  const Icon = isInvoice ? ReceiptIcon : FileText;
  const accent = isInvoice ? "#10B981" : "#1FB6C1";
  const labelKind = isInvoice ? "Invoice" : "Quote";
  const paid = doc.payment_status === "paid";

  return (
    <div className="min-h-screen bg-[#f7f8fb] text-gray-900 py-8 print:bg-white print:py-0 hv-safe-top" data-testid={`portal-${kind}-page`}>
      <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden print:border-0 print:shadow-none print:rounded-none">
        {/* Header band */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-3 flex-wrap" style={{ background: `linear-gradient(135deg, ${accent}11, transparent)` }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${accent}22` }}>
              <Icon className="w-5 h-5" style={{ color: accent }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">{labelKind}</div>
              <h1 className="font-display text-2xl font-bold text-gray-900 leading-tight">{doc.document_number || doc.title}</h1>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Issued to</div>
            <div className="font-semibold text-gray-900">{customer?.name || "—"}</div>
          </div>
        </div>

        {/* Status row */}
        <div className="px-6 py-3 border-b border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <KV label="Document #" value={doc.document_number || "—"} />
          <KV label="Issued" value={doc.document_date || "—"} />
          <KV label={isInvoice ? "Due" : "Valid until"} value={doc.due_date || "—"} />
          <KV
            label="Status"
            value={
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
                style={{
                  background: paid ? "#10B98122" : `${accent}22`,
                  color: paid ? "#059669" : accent,
                }}
                data-testid="portal-status-pill"
              >
                {paid && <CheckCircle2 className="w-2.5 h-2.5" />} {paid ? "Paid" : (doc.status || "Sent")}
              </span>
            }
          />
        </div>

        {/* Line items */}
        {doc.line_items?.length > 0 && (
          <div className="px-6 py-4 border-b border-gray-100">
            <table className="w-full text-sm" data-testid="portal-line-items">
              <thead className="text-gray-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2">Description</th>
                  <th className="text-right py-2 w-16">Qty</th>
                  <th className="text-right py-2 w-24">Unit</th>
                  <th className="text-right py-2 w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {doc.line_items.map((li, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="py-2 pr-2 text-gray-900">{li.description}</td>
                    <td className="py-2 text-right text-gray-700">{li.qty}</td>
                    <td className="py-2 text-right text-gray-700 font-mono">{fmtMoney(li.unit_price, doc.currency)}</td>
                    <td className="py-2 text-right text-gray-900 font-mono">{fmtMoney(li.total, doc.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Total band */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100 bg-gray-50">
          <span className="text-sm uppercase tracking-widest text-gray-500">Total</span>
          <span className="font-display text-2xl font-bold" style={{ color: accent }} data-testid="portal-total">
            {fmtMoney(doc.amount, doc.currency)}
          </span>
        </div>

        {/* Pay button */}
        {isInvoice && !paid && doc.payment_link && (
          <div className="px-6 py-4 border-b border-gray-100">
            <a
              href={doc.payment_link}
              target="_blank"
              rel="noreferrer"
              className="block w-full text-center py-3 rounded-lg font-semibold text-white"
              style={{ background: "#10B981" }}
              data-testid="portal-pay-btn"
            >
              Pay this invoice →
            </a>
          </div>
        )}

        {/* Download PDF */}
        {doc.file_url && (
          <div className="px-6 py-4 border-b border-gray-100">
            <a
              href={doc.file_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
              style={{ color: accent }}
              data-testid="portal-download-pdf"
            >
              <Download className="w-4 h-4" /> Download PDF copy
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 text-[11px] text-gray-500 flex items-center justify-between flex-wrap gap-2">
          <span>This is a secure link from {brandName}. It expires after 3 days.</span>
          <a href={`mailto:${supportEmail}`} className="underline">{supportEmail}</a>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 font-medium mt-0.5">{value}</div>
    </div>
  );
}
